import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { gettingStartedGuard } from './guards/getting-started.guard';
import { safetyTipsGuard } from './guards/safety-tips.guard';
import { subscriptionGuard } from './guards/subscription.guard';
import { termsGuard } from './guards/terms.guard';

export const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('src/app/auth/auth/auth.routes').then(m => m.AUTH_ROUTES),
  },
  {
    path: 'home',
    loadComponent: () => import('src/app/pages/home/home.page').then(m => m.HomePage),
    canActivate: [authGuard, termsGuard, subscriptionGuard],
  },
  {
    path: 'chats',
    loadComponent: () => import('./pages/chats/chats.page').then(m => m.ChatsPage),
    canActivate: [authGuard, termsGuard, subscriptionGuard],
  },
  {
    path: 'explore',
    children: [
      {
        path: '',
        loadComponent: () => import('src/app/pages/explore/explore.page').then(m => m.ExplorePage),
      },
      {
        path: 'listing/:id',
        loadComponent: () => import('src/app/pages/listing/listing-detail/listing-detail.page').then(m => m.ListingDetailPage),
      },
    ],
    canActivate: [authGuard, termsGuard, subscriptionGuard],
  },
  {
    path: 'trades',
    children: [
      {
        path: '',
        redirectTo: 'want/pending',
        pathMatch: 'full',
      },
      {
        path: 'propose/:listingId',
        loadComponent: () => import('src/app/pages/trades/propose-trade/propose-trade.page').then(m => m.ProposeTradePage),
      },
      {
        path: 'listing/:id',
        loadComponent: () => import('src/app/pages/listing/listing-detail/listing-detail.page').then(m => m.ListingDetailPage),
      },
      {
        path: ':side/:status',
        loadComponent: () => import('src/app/pages/trades/trades.page').then(m => m.TradesPage),
      },
      {
        path: ':id',
        loadComponent: () => import('src/app/pages/trade-detail/trade-detail.page').then(m => m.TradeDetailPage),
      },
    ],
    canActivate: [authGuard, termsGuard, subscriptionGuard],
  },
  {
    path: 'business',
    children: [
      {
        path: '',
        loadComponent: () => import('src/app/pages/business/business.page').then(m => m.BusinessPage),
      },
      {
        path: 'listing-matching-preview',
        loadComponent: () => import('src/app/pages/business/matching-preview/matching-preview.page').then(m => m.MatchingPreviewPage),
      },
      {
        path: 'create',
        loadComponent: () => import('src/app/pages/business/create/create-business.page').then(m => m.CreateBusinessPage),
      },
      {
        path: 'edit',
        loadComponent: () => import('src/app/pages/business/create/create-business.page').then(m => m.CreateBusinessPage),
      },
      {
        path: 'create-listing',
        loadComponent: () => import('src/app/pages/listing/create-listing/create-listing.page').then(m => m.CreateListingPage),
      },
      {
        path: 'listing/:id/edit',
        loadComponent: () => import('src/app/pages/listing/edit-listing/edit-listing.page').then(m => m.EditListingPage),
      },
      {
        path: 'listing/:id',
        loadComponent: () => import('src/app/pages/listing/listing-detail/listing-detail.page').then(m => m.ListingDetailPage),
      },
    ],
    canActivate: [authGuard, termsGuard, subscriptionGuard],
  },
  {
    path: 'account',
    children: [
      {
        path: '',
        loadComponent: () => import('src/app/pages/account-overview/account-overview.page').then(m => m.AccountOverviewPage),
      },
      {
        path: 'subscription',
        loadComponent: () => import('src/app/pages/subscription/subscription.page').then(m => m.SubscriptionPage),
        canActivate: [termsGuard],
      },
      {
        path: 'profile',
        loadComponent: () => import('src/app/auth/account/account.page').then(m => m.AccountPage),
      },
      {
        path: 'notifications',
        loadComponent: () => import('src/app/pages/notifications/notifications.page').then(m => m.NotificationsPage),
      },
      {
        path: 'settings',
        loadComponent: () => import('src/app/pages/settings/settings.page').then(m => m.SettingsPage),
      },
      {
        path: 'help',
        children: [
          {
            path: '',
            loadComponent: () => import('src/app/pages/help-support/help-support.page').then(m => m.HelpSupportPage),
          },
          {
            path: 'topic/getting-started',
            loadComponent: () => import('src/app/pages/getting-started/getting-started.page').then(m => m.GettingStartedPage),
            canActivate: [gettingStartedGuard],
          },
          {
            path: 'topic/:slug',
            loadComponent: () => import('src/app/pages/help-topic-detail/help-topic-detail.page').then(m => m.HelpTopicDetailPage),
          },
          {
            path: 'contact-support',
            loadComponent: () => import('src/app/pages/contact-support/contact-support.page').then(m => m.ContactSupportPage),
          },
          {
            path: 'report-problem',
            loadComponent: () => import('src/app/pages/report-problem/report-problem.page').then(m => m.ReportProblemPage),
          },
          {
            path: 'safety',
            loadComponent: () => import('src/app/pages/safety-tips/safety-tips.page').then(m => m.SafetyTipsPage),
            canActivate: [safetyTipsGuard],
          },
        ],
      },
    ],
    canActivate: [authGuard],
  },
  {
    path: 'getting-started',
    loadComponent: () => import('src/app/pages/getting-started/getting-started.page').then(m => m.GettingStartedPage),
    canActivate: [authGuard, termsGuard, subscriptionGuard, gettingStartedGuard],
  },
  {
    path: 'legal/accept',
    loadComponent: () => import('src/app/pages/legal/accept-terms.page').then(m => m.AcceptTermsPage),
    canActivate: [authGuard],
  },
  {
    path: 'privacy',
    loadComponent: () => import('src/app/pages/legal/privacy.page').then(m => m.PrivacyPage),
  },
  {
    path: 'terms',
    loadComponent: () => import('src/app/pages/legal/terms.page').then(m => m.TermsPage),
  },
  {
    path: 'cookie-policy',
    loadComponent: () => import('src/app/pages/legal/cookie-policy.page').then(m => m.CookiePolicyPage),
  },
  {
    path: 'annex-1',
    loadComponent: () => import('src/app/pages/legal/annex-1.page').then(m => m.Annex1Page),
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
];
